import { useEffect, useRef, useState } from 'react';

import { upbitSocketManager } from '../lib/upbitSocket';
import { useRealtimeGaveUpToast } from './useRealtimeGaveUpToast';

import type { UpbitTicker } from '../types';

interface UseUpbitWebSocketProps {
  markets: string[];
  onTickerUpdate: (ticker: UpbitTicker) => void;
  enabled?: boolean;
}

const parseUpbitMessage = async (data: Blob): Promise<UpbitTicker | null> => {
  try {
    const text = await data.text();
    const parsed = JSON.parse(text);

    if (parsed.type !== 'ticker') {
      return null;
    }

    const calculatedChangeRate = parsed.prev_closing_price
      ? (parsed.trade_price - parsed.prev_closing_price) / parsed.prev_closing_price
      : 0;

    return {
      market: parsed.code,
      trade_price: parsed.trade_price,
      opening_price: parsed.opening_price,
      high_price: parsed.high_price,
      low_price: parsed.low_price,
      prev_closing_price: parsed.prev_closing_price,
      change: parsed.change,
      change_price: parsed.change_price,
      change_rate: calculatedChangeRate,
      signed_change_price: parsed.signed_change_price,
      signed_change_rate: parsed.signed_change_rate ?? calculatedChangeRate,
      acc_trade_volume_24h: parsed.acc_trade_volume_24h,
      acc_trade_price_24h: parsed.acc_trade_price_24h,
      highest_52_week_price: parsed.highest_52_week_price,
      highest_52_week_date: parsed.highest_52_week_date,
      lowest_52_week_price: parsed.lowest_52_week_price,
      lowest_52_week_date: parsed.lowest_52_week_date,
    };
  } catch (error) {
    // 🔴 raw error를 삼키지 않는다 — 원인 불명 파싱 실패가 조용히 무시되면 "화면이 안 갱신되는데
    // 콘솔엔 아무것도 없다"는 증상을 만든다(TRAD-3045 조사에서 지적된 패턴).
    console.error('[useUpbitWebSocket] ticker parse failed', error);
    return null;
  }
};

export const useUpbitWebSocket = ({ markets, onTickerUpdate, enabled = true }: UseUpbitWebSocketProps) => {
  const onTickerUpdateRef = useRef(onTickerUpdate);
  const [isConnected, setIsConnected] = useState(false);

  useRealtimeGaveUpToast(upbitSocketManager, enabled && markets.length > 0);

  useEffect(() => {
    onTickerUpdateRef.current = onTickerUpdate;
  }, [onTickerUpdate]);

  useEffect(() => {
    if (!enabled || markets.length === 0) return;

    const handleMessage = async (data: string | ArrayBuffer | Blob) => {
      const ticker = await parseUpbitMessage(data as Blob);
      if (ticker) {
        onTickerUpdateRef.current(ticker);
      }
    };

    const unsubscribe = upbitSocketManager.subscribe('ticker', markets, handleMessage);
    const unlistenConnection = upbitSocketManager.onConnectionChange(setIsConnected);
    setIsConnected(upbitSocketManager.connected);

    return () => {
      unsubscribe();
      unlistenConnection();
    };
  }, [enabled, markets.join(',')]);

  return { isConnected };
};
